import Box from '@mui/material/Box';
import {getBreadCrumbsProps} from '../../../utils/resource-utils';
import {uuid} from '../../../utils/utils';
import React, {ReactElement, useCallback, useEffect} from 'react';
import {ResourceType} from '../../../@types/resource-type';
import {SelectedWorkspaceObject} from '../../../@types/workspace-types';
import TabItem from '../../../@types/tab-item';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import {ResourceAction} from '../../../@types/resource-action';
import {useTabState} from './useTabState';
import {createTabPersistence} from './tab-persistence';
import {resolveAdditionalInformation, resolveTabLabel} from './tab-editor-utils';
import {TabBar} from './TabBar.component';
import {EditorContainer} from './EditorContainer.component';

interface TabEditorProps {
    objectToEdit: SelectedWorkspaceObject | null;
    onTabChange: (object: any) => void;
}


/*
    This is a generic Tab Editor component that can be used to edit any type of resource.
*/
export default function TabEditor(props: TabEditorProps): ReactElement {
    const {objectToEdit, onTabChange} = props;
    const {deletedWorkspaceObject, currentAction, setCurrentAction, getAdditionalResource} = useWorkspaceContext();
    const uuidRef = React.useRef(uuid());
    
    // Track processed deleted objects to prevent duplicate handling
    const processedDeletedObjectsRef = React.useRef<Set<string>>(new Set());
    
    // Track if we need to notify parent after deletion
    const needsPostDeletionNotification = React.useRef<boolean>(false);

    // Initialize persistence
    const persistence = React.useMemo(() => createTabPersistence('sessionStorage'), []);

    // Initialize tab state with persistence
    const {
        tabs: tabItems,
        activeIndex: currentTabIndex,
        addTab,
        removeTab,
        updateTab,
        updateAndSynchronizeTab,
        setActiveIndex,
        findTab,
        removeTabsForDeletedObject,
        synchronizeTabs
    } = useTabState(persistence.loadTabs(), persistence.loadActiveIndex(), (tabs) => persistence.saveTabs(tabs), (index) => persistence.saveActiveIndex(index));

    // Wrapper for resolving additional information with workspace context
    const addAdditionalInformation = useCallback((tabObject: { objectType: ResourceType, object: any }): any => {
        return resolveAdditionalInformation(tabObject, getAdditionalResource);
    }, [getAdditionalResource]);

    const handleTabChange = useCallback((event: React.SyntheticEvent, newTabIndex: number): void => {
        setActiveIndex(newTabIndex);
        const tab = tabItems[newTabIndex];
        if (tab) {
            onTabChange({objectType: tab.type, object: tab.objectToEdit});
        }
    }, [setActiveIndex, tabItems, onTabChange]);

    const handleTabClose = useCallback((event: React.SyntheticEvent, tabToDelete: TabItem): void => {
        event.stopPropagation();

        const isClosingActiveTab = tabToDelete.index === currentTabIndex;
        removeTab(tabToDelete.index);

        // Only notify parent if we're closing the active tab
        if (isClosingActiveTab) {
            const remainingTabs = tabItems.filter(tab => tab.index !== tabToDelete.index);
            const newIndex = tabToDelete.index === 0 ? 0 : tabToDelete.index - 1;
            const newActiveTab = remainingTabs[newIndex];

            if (newActiveTab) {
                onTabChange({objectType: newActiveTab.type, object: newActiveTab.objectToEdit});
            } else {
                onTabChange(null);
            }
        }
    }, [removeTab, tabItems, currentTabIndex, onTabChange]);

    const handleUpdateTab = useCallback((tabObject: { objectType: ResourceType, object: any }): void => {
        const objectWithInfo = addAdditionalInformation(tabObject);

        updateAndSynchronizeTab(currentTabIndex, {
            label: resolveTabLabel(tabObject.objectType, objectWithInfo),
            docId: tabObject.object.id ?? tabObject.object.uniqueId,
            breadCrumbs: getBreadCrumbsProps(objectWithInfo, tabObject.objectType),
            objectToEdit: tabObject.object
        }, tabObject.objectType);
        
    }, [currentTabIndex, updateTab, addAdditionalInformation, synchronizeTabs]);

    // Handle deleted workspace objects
    useEffect(() => {
        if (deletedWorkspaceObject) {
            // Create a unique key for this deletion event
            const deletionKey = `${deletedWorkspaceObject.objectType}-${deletedWorkspaceObject.id}-${Date.now()}`;
            
            
            // Check if we've already processed this exact deletion event
            if (processedDeletedObjectsRef.current.has(deletionKey)) {
                return;
            }
            
            // Mark as processed
            processedDeletedObjectsRef.current.add(deletionKey);
            
            // Clear old entries after a delay to prevent memory leak
            setTimeout(() => {
                processedDeletedObjectsRef.current.delete(deletionKey);
            }, 5000);
            
            const childrenIds = deletedWorkspaceObject.children ?? [];
            
            // Remove the tabs
            removeTabsForDeletedObject(deletedWorkspaceObject.id, childrenIds);
            
            // Mark that we need to notify parent after state updates
            needsPostDeletionNotification.current = true;
        }
    }, [deletedWorkspaceObject]);

    // Handle post-deletion notification
    useEffect(() => {
        if (needsPostDeletionNotification.current && tabItems) {
            needsPostDeletionNotification.current = false;
            
            // Find the current active tab
            const activeTab = tabItems.find(tab => tab.index === currentTabIndex);
            
            if (activeTab) {
                onTabChange({objectType: activeTab.type, object: activeTab.objectToEdit});
            } else if (tabItems.length > 0) {
                onTabChange({objectType: tabItems[0].type, object: tabItems[0].objectToEdit});
            } else {
                onTabChange(null);
            }
        }
    }, [tabItems, currentTabIndex, onTabChange]);

    // Track the last processed objectToEdit to prevent duplicate processing
    const lastProcessedObjectRef = React.useRef<{ type?: ResourceType, id?: any, timestamp?: number }>({});

    // Handle object to edit changes
    useEffect(() => {

        // For new objects without ID, use a combination of type and timestamp to track
        const docKey = objectToEdit?.object?.id ?? objectToEdit?.object?.uniqueId;
        const currentObjectKey = objectToEdit ? {
            type: objectToEdit.objectType,
            id: docKey,
            timestamp: docKey ? 0 : Date.now()
        } : null;

        // Check if this is effectively the same object we already processed
        const isSameObject = currentObjectKey && lastProcessedObjectRef.current.type === currentObjectKey.type && lastProcessedObjectRef.current.id === currentObjectKey.id && (currentObjectKey.id ? true : // For objects with ID, just check type and ID match
            (Date.now() - (lastProcessedObjectRef.current.timestamp || 0)) < 1000); // For new objects, check if processed within last second

        // Skip if we've already processed this exact object (but only if we have tabs)
        if (isSameObject && tabItems.length > 0) {
            return;
        }

        // Check if this object was just deleted - don't try to add a tab for it
        // Commented out the test for deletedWorkspaceObject.objectType as it was causing issue 1445
        if (deletedWorkspaceObject && objectToEdit &&
            //deletedWorkspaceObject.objectType === objectToEdit.objectType &&
            deletedWorkspaceObject.id === objectToEdit.object?.id) {
            return;
        }


        if (objectToEdit && objectToEdit.object) {
            // Update our tracking ref
            lastProcessedObjectRef.current = currentObjectKey || {};

            const existingTab = findTab(objectToEdit.objectType, docKey);

            if (existingTab) {
                // only updates if existingTab index matches currentTabIndex
                // otherwise, we just switch to the existing tab
                // This fixes issue 1444
                if (existingTab.index === currentTabIndex) {
                    const objectWithInfo = addAdditionalInformation(objectToEdit);

                    updateAndSynchronizeTab(currentTabIndex, {
                        label: resolveTabLabel(objectToEdit.objectType, objectWithInfo),
                        docId: docKey,
                        breadCrumbs: getBreadCrumbsProps(objectWithInfo, objectToEdit.objectType),
                        objectToEdit: objectToEdit.object
                    }, objectToEdit.objectType);

                }
                // Switch to the existing tab
                setActiveIndex(existingTab.index);
            } else if (!docKey) {
                // For new objects without ID, check if we already have a tab for this type without an ID
                const existingNewTab = tabItems.find(tab => tab.type === objectToEdit.objectType && (!tab.docId || tab.docId === undefined));

                if (!existingNewTab) {
                    // Only add new tab if we don't already have one
                    addTab(objectToEdit.objectType, objectToEdit.object, getAdditionalResource);
                }
            } else {
                // Object has an ID but no existing tab - we should add it
                addTab(objectToEdit.objectType, objectToEdit.object, getAdditionalResource);
            }
        } else if (tabItems && tabItems.length > 0 && currentTabIndex >= 0 && !objectToEdit) {
            // Only call onTabChange if we don't have an objectToEdit (to avoid loops)
            const activeTab = tabItems[currentTabIndex];
            if (activeTab) {
                onTabChange({objectType: activeTab.type, object: activeTab.objectToEdit});
            }
        }
    }, [objectToEdit, findTab, updateTab, synchronizeTabs, setActiveIndex, addTab, getAdditionalResource, tabItems, currentTabIndex, onTabChange]);

    // Handle close action from workspace context
    useEffect(() => {
        if (currentAction === ResourceAction.CLOSE && tabItems && tabItems.length > 0) {
            setCurrentAction(null);
            const tabToClose = tabItems[currentTabIndex];
            if (tabToClose) {
                handleTabClose(new Event('click') as any, tabToClose);
            }
        }
    }, [currentAction, tabItems, currentTabIndex, setCurrentAction, handleTabClose]);


    return (<Box sx={{display: 'flex', flexDirection: 'column', height: '100%', width: '100%'}}>
        <TabBar
            tabs={tabItems}
            activeIndex={currentTabIndex}
            onTabChange={handleTabChange}
            onTabClose={handleTabClose}
            componentId={uuidRef.current}
        />
        {/*
          IMPORTANT: We use tab.uid as the key instead of tab.index.
          When a tab is closed, remaining tabs are reindexed (e.g., tab at index 3 becomes index 2).
          If we used index as the key, React would see the key change and unmount/remount the component,
          causing unnecessary re-renders, data loss, and re-fetching of content.
          The uid is a stable identifier generated once when the tab is created and never changes,
          so React can correctly preserve component instances when other tabs are closed.
        */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {tabItems.map((tab: TabItem) => (<EditorContainer
                key={tab.uid}
                tab={tab}
                isActive={tab.index === currentTabIndex}
                currentIndex={currentTabIndex}
                onUpdate={handleUpdateTab}
                componentId={uuidRef.current}
            />))}
        </Box>
    </Box>);
}
